import { useState, useRef } from 'react';
import { useTheme, themeColors } from '../ThemeContext';
import { booths, Booth } from '../../../mocks/boothData';
import { useTranslation } from 'react-i18next';

interface ExpoMapScreenProps {
  onBoothSelect: (booth: Booth) => void;
  boothStates: Record<string, Booth['status']>;
  onShowAI: () => void;
  onShowProgress: () => void;
  onShowNotes: () => void;
}

const HALLS = ['Hall A', 'Hall B', 'Hall C', 'Hall D'];
const hallColors = {
  dark: { 'Hall A': '#00d4ff', 'Hall B': '#a78bfa', 'Hall C': '#34d399', 'Hall D': '#fb923c' },
  light: { 'Hall A': '#0070E0', 'Hall B': '#7C3AED', 'Hall C': '#059669', 'Hall D': '#D97706' },
};

const CELL = 52;
const GRID_H = 8;

const statusStyle = (status: Booth['status'], isDark: boolean, sponsorLevel: string) => {
  if (sponsorLevel === 'none') return isDark ? 'opacity-40 border border-gray-700 bg-gray-800/60' : 'opacity-40 border border-gray-200 bg-gray-100';
  switch (status) {
    case 'visited': return isDark ? 'border-2 border-emerald-500 bg-emerald-900/50' : 'border-2 border-emerald-500 bg-emerald-50';
    case 'recommended': return isDark ? 'border-2 border-[#a78bfa] bg-[#a78bfa]/20 shadow-[0_0_12px_rgba(167,139,250,0.5)] animate-pulse' : 'border-2 border-purple-500 bg-purple-50 shadow-[0_0_12px_rgba(124,58,237,0.3)] animate-pulse';
    case 'important': return isDark ? 'border-2 border-amber-400 bg-amber-900/40 shadow-[0_0_10px_rgba(251,191,36,0.4)]' : 'border-2 border-amber-500 bg-amber-50 shadow-[0_0_10px_rgba(217,119,6,0.2)]';
    default: return isDark ? 'border border-[#1e2d47] bg-[#111827]' : 'border border-gray-200 bg-white';
  }
};

const ExpoMapScreen = ({ onBoothSelect, boothStates, onShowAI, onShowProgress, onShowNotes }: ExpoMapScreenProps) => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();
  const [activeHall, setActiveHall] = useState<string>('Hall A');
  const [scale, setScale] = useState(1);
  const mapRef = useRef<HTMLDivElement>(null);

  const hallBooaths = booths.filter(b => b.hall === activeHall);
  const hallColor = hallColors[theme][activeHall as keyof typeof hallColors.dark] || '#00d4ff';
  const visitedCount = Object.values(boothStates).filter(s => s === 'visited').length;
  const totalCount = booths.length;
  const progressPct = Math.round((visitedCount / totalCount) * 100);

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg} overflow-hidden`}>
      <div className={`sticky top-0 z-20 ${c.navBg} backdrop-blur-xl border-b ${c.navBorder} px-5 pt-12 pb-3`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className={`text-xl font-black ${c.text}`}>{t('map.title')}</h1>
            <p className={`text-xs ${c.textMuted}`}>{t('map.subtitle')}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setScale(s => Math.min(s + 0.2, 1.8))} className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm cursor-pointer whitespace-nowrap border ${c.bgCard} ${c.border} ${c.textMuted}`}><i className="ri-zoom-in-line"></i></button>
            <button onClick={() => setScale(s => Math.max(s - 0.2, 0.6))} className={`w-8 h-8 flex items-center justify-center rounded-xl text-sm cursor-pointer whitespace-nowrap border ${c.bgCard} ${c.border} ${c.textMuted}`}><i className="ri-zoom-out-line"></i></button>
          </div>
        </div>

        <div className={`flex items-center gap-3 p-2.5 rounded-xl mb-3 border ${isDark ? 'bg-[#00d4ff]/10 border-[#00d4ff]/20' : 'bg-blue-50 border-blue-100'}`}>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[11px] font-bold ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}>{t('map.coverage')}</span>
              <span className={`text-sm font-black ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}`}>{progressPct}%</span>
            </div>
            <div className={`h-1.5 rounded-full ${isDark ? 'bg-[#1e2d47]' : 'bg-blue-100'}`}>
              <div className={`h-full rounded-full transition-all duration-700 ${isDark ? 'bg-gradient-to-r from-[#00d4ff] to-[#a78bfa]' : 'bg-gradient-to-r from-[#0070E0] to-[#7C3AED]'}`} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          <div className={`text-[10px] ${isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'} font-semibold`}>{visitedCount}/{totalCount}</div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {HALLS.map(h => {
            const hc = hallColors[theme][h as keyof typeof hallColors.dark];
            const isActive = activeHall === h;
            return (
              <button key={h} onClick={() => setActiveHall(h)}
                className="px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 cursor-pointer border"
                style={{ background: isActive ? hc : 'transparent', color: isActive ? (isDark ? '#08090F' : '#fff') : hc, borderColor: hc + (isActive ? 'ff' : '60') }}>
                {h}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-auto px-5 pt-4" ref={mapRef}>
        <div className="flex gap-3 flex-wrap mb-3">
          {[
            { color: isDark ? '#34d399' : '#059669', border: isDark ? 'border-emerald-500' : 'border-emerald-500', label: t('map.legend.visited') },
            { color: isDark ? '#a78bfa' : '#7C3AED', border: isDark ? 'border-[#a78bfa]' : 'border-purple-500', label: t('map.legend.recommended') },
            { color: isDark ? '#fbbf24' : '#D97706', border: isDark ? 'border-amber-400' : 'border-amber-500', label: t('map.legend.important') },
            { color: isDark ? '#374151' : '#d1d5db', border: isDark ? 'border-gray-700' : 'border-gray-200', label: t('map.legend.nonsponsored') },
          ].map(l => (
            <div key={l.label} className="flex items-center gap-1">
              <div className={`w-3 h-3 rounded border-2 ${l.border}`} style={{ background: l.color + '40' }}></div>
              <span className={`text-[10px] ${c.textMuted}`}>{l.label}</span>
            </div>
          ))}
        </div>

        <div className={`relative rounded-2xl overflow-hidden border ${c.border} mb-4`} style={{ minHeight: GRID_H * CELL + 48 }}>
          <div className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-xs font-black" style={{ background: hallColor + '25', color: hallColor, border: `1px solid ${hallColor}60` }}>{activeHall}</div>
          <div className={`absolute inset-0 ${isDark ? 'bg-[#0D1117]' : 'bg-[#F0F4FF]'}`} style={{ backgroundImage: `linear-gradient(${hallColor}12 1px, transparent 1px), linear-gradient(90deg, ${hallColor}12 1px, transparent 1px)`, backgroundSize: `${CELL}px ${CELL}px`, transform: `scale(${scale})`, transformOrigin: 'top left' }} />
          <div className="absolute rounded" style={{ left: CELL * 3.5, top: CELL * 0.5, width: CELL * 0.8, height: GRID_H * CELL - CELL, background: isDark ? 'rgba(0,212,255,0.06)' : 'rgba(0,112,224,0.06)', border: `1px dashed ${hallColor}30`, transform: `scale(${scale})`, transformOrigin: 'top left' }} />
          <div className="absolute inset-0" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
            {hallBooaths.map(booth => {
              const status = boothStates[booth.id] || booth.status;
              const style = statusStyle(status, isDark, booth.sponsorLevel);
              const isBlurred = booth.sponsorLevel === 'none';
              return (
                <button key={booth.id} onClick={() => onBoothSelect({ ...booth, status })}
                  className={`absolute rounded-lg transition-all duration-200 active:scale-95 cursor-pointer flex flex-col items-center justify-center text-center px-1 overflow-hidden ${style}`}
                  style={{ left: booth.gridX * CELL + 8, top: booth.gridY * CELL + 36, width: booth.width * CELL - 8, height: booth.height * CELL - 8 }}>
                  {status === 'visited' && <div className="absolute top-1 right-1"><i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i></div>}
                  {status === 'important' && <div className="absolute top-1 right-1"><i className="ri-star-fill text-amber-400 text-xs"></i></div>}
                  {booth.sponsorLevel === 'platinum' && <div className="absolute top-1 left-1"><span className="text-[8px] font-bold text-slate-300">✦</span></div>}
                  <p className={`text-[10px] font-bold leading-tight ${isBlurred ? 'blur-[2px]' : ''} ${isDark ? 'text-gray-200' : 'text-gray-700'}`} style={{ color: status === 'recommended' ? (isDark ? '#a78bfa' : '#7C3AED') : undefined }}>
                    {booth.company.split(' ').slice(0, 2).join(' ')}
                  </p>
                  <p className="text-[9px] mt-0.5 font-semibold" style={{ color: hallColor + 'cc' }}>{booth.boothNumber}</p>
                </button>
              );
            })}
          </div>
          <div className={`absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 text-[10px] font-bold px-3 py-1 rounded-full ${isDark ? 'bg-[#00d4ff]/20 text-[#00d4ff] border border-[#00d4ff]/30' : 'bg-blue-100 text-blue-700 border border-blue-200'}`}>
            <i className="ri-door-open-line text-sm"></i> {t('map.entrance')}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {[
            { icon: 'ri-checkbox-circle-line', color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', label: t('map.stat.visited'), count: hallBooaths.filter(b => (boothStates[b.id] || b.status) === 'visited').length },
            { icon: 'ri-sparkling-line', color: isDark ? 'text-[#a78bfa]' : 'text-purple-600', bg: isDark ? 'bg-[#a78bfa]/10' : 'bg-purple-50', label: t('map.stat.suggested'), count: hallBooaths.filter(b => (boothStates[b.id] || b.status) === 'recommended').length },
            { icon: 'ri-building-2-line', color: isDark ? 'text-[#00d4ff]' : 'text-blue-600', bg: isDark ? 'bg-[#00d4ff]/10' : 'bg-blue-50', label: t('map.stat.total'), count: hallBooaths.length },
          ].map(stat => (
            <div key={stat.label} className={`flex flex-col items-center gap-1 p-3 rounded-xl ${stat.bg} border ${c.border}`}>
              <i className={`${stat.icon} text-lg ${stat.color}`}></i>
              <span className={`text-lg font-black ${c.text}`}>{stat.count}</span>
              <span className={`text-[10px] ${c.textMuted}`}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed z-40 flex flex-col gap-3" style={{ bottom: '88px', right: 'calc(50% - 224px)' }}>
        <button onClick={onShowAI} className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all active:scale-90 cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#a78bfa] text-white shadow-[0_0_20px_rgba(167,139,250,0.4)]' : 'bg-purple-600 text-white'}`}><i className="ri-sparkling-2-line text-xl"></i></button>
        <button onClick={onShowNotes} className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all active:scale-90 cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#1e2d47] text-[#00d4ff] border border-[#00d4ff]/30' : 'bg-white text-blue-600 border border-blue-100'}`}><i className="ri-sticky-note-2-line text-xl"></i></button>
        <button onClick={onShowProgress} className={`w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-all active:scale-90 cursor-pointer whitespace-nowrap ${isDark ? 'bg-[#1e2d47] text-[#00d4ff] border border-[#00d4ff]/30' : 'bg-white text-blue-600 border border-blue-100'}`}><i className="ri-bar-chart-grouped-line text-xl"></i></button>
      </div>
    </div>
  );
};

export default ExpoMapScreen;
